import React, { useRef, useMemo, useCallback } from 'react';
import {
  CartesianGrid,
  Line,
  LineChart,
  XAxis,
  YAxis,
  Tooltip,
} from 'recharts';
import { parseISO } from 'date-fns';
import { ChartContainer } from '@ais-ui/ui-core';
import { ShipTrackProps, ChartDataPoint } from './types';
import { cn } from '@ais-ui/utils';
import { useDateTimeFormat } from '../../../conversions/useDateFormat';

// Updated interface to match what Recharts expects
interface CustomDotProps {
  cx?: number;
  cy?: number;
  index?: number;
  payload?: ChartDataPoint;
  r?: number;
  value?: number;
  dataKey?: string;
  stroke?: string;
  strokeWidth?: number;
  fill?: string;
  strokeDasharray?: string;
  className?: string;
}

interface CustomTooltipContentProps {
  active?: boolean;
  payload?: Array<{ value: number }>;
  label?: string;
}

// Extend ShipTrackProps to include dataType
interface ExtendedShipTrackProps extends ShipTrackProps {
  dataType?: 'sog' | 'cog';
}

export default function ShipTrackChart({
  trackData,
  hoveredPointIndex,
  onPointHover,
  className,
  dataType = 'sog', // Default to speed over ground
}: ExtendedShipTrackProps): React.JSX.Element {
  const chartRef = useRef<any>(null);

  // Get the label for the current data type
  const dataLabel = useMemo(() => {
    return dataType === 'sog' ? 'Speed (knots)' : 'Course (degrees)';
  }, [dataType]);

  // Transform track data for chart display
  const chartData = useMemo((): ChartDataPoint[] => {
    if (!trackData?.track?.coordinates?.length) {
      return [];
    }

    // Extract timestamp and either speed or course from each track point
    return trackData.track.coordinates
      .map((point, index) => {
        if (Array.isArray(point) && point.length >= 5) {
          // For SOG, use point[4], for COG use point[3] (course)
          return {
            index,
            timestamp: String(point[2]), // Ensure timestamp is a string
            value: dataType === 'sog' ? Number(point[4]) : Number(point[3]), // Speed from index 4, course from index 3
            valueLabel: dataType, // Store the type of value for tooltip
          };
        }
        return null;
      })
      .filter((item): item is ChartDataPoint => item !== null);
  }, [trackData, dataType]);
  const { formatDateTimeWithTimeZone } = useDateTimeFormat();

  // Format x-axis ticks
  const formatXAxis = (timestamp: string): string => {
    try {
      const date = parseISO(timestamp);
      return formatDateTimeWithTimeZone(date).slice(10, 16);
    } catch (error) {
      console.error('Error formatting date:', error);
      return timestamp;
    }
  };

  // Format tooltip timestamp
  const formatTooltipDate = (timestamp: string): string => {
    try {
      const date = parseISO(timestamp);
      return formatDateTimeWithTimeZone(date);
    } catch (error) {
      console.error('Error formatting tooltip date:', error);
      return timestamp;
    }
  };

  // Calculate y-axis domain based on data
  const yAxisDomain = useMemo((): [number, number] => {
    if (!chartData.length) {
      // Default domains based on data type
      return dataType === 'sog' ? [0, 20] : [0, 360];
    }

    const values = chartData.map((item) => item.value);

    if (dataType === 'cog') {
      // Course is always between 0-360 degrees
      return [0, 360];
    } else {
      // For speed, calculate a reasonable range
      const minVal = Math.floor(Math.max(0, Math.min(...values) - 2));
      const maxVal = Math.ceil(Math.max(...values) + 2);
      return [minVal, maxVal];
    }
  }, [chartData, dataType]);

  // Handle chart hover events
  const handleChartMouseMove = useCallback(
    (e: any): void => {
      if (e?.activeTooltipIndex !== undefined) {
        onPointHover(e.activeTooltipIndex);
      }
    },
    [onPointHover],
  );

  const handleChartMouseLeave = useCallback((): void => {
    onPointHover(null);
  }, [onPointHover]);

  // Custom dot component for the chart
  const CustomDot = (props: CustomDotProps): React.JSX.Element => {
    const { cx = 0, cy = 0, index = 0 } = props;
    const isHovered = index === hoveredPointIndex;

    return (
      <circle
        cx={cx}
        cy={cy}
        r={isHovered ? 8 : 4}
        fill={isHovered ? '#00A3C4' : 'var(--color-data, #00647a)'}
        stroke={isHovered ? 'white' : 'none'}
        strokeWidth={isHovered ? 2 : 0}
        style={{ transition: 'all 0.2s ease' }}
      />
    );
  };

  // Custom tooltip content component
  const CustomTooltipContent = ({
    active,
    payload,
    label,
  }: CustomTooltipContentProps): React.JSX.Element | null => {
    const isActive = active || hoveredPointIndex !== null;

    if (isActive && payload?.length) {
      const pointIndex = active ? undefined : hoveredPointIndex;
      const currentPoint =
        pointIndex !== undefined && pointIndex !== null
          ? chartData[pointIndex]
          : null;

      // Use the correct label and value depending on where the hover comes from
      const displayLabel = currentPoint ? currentPoint.timestamp : label || '';
      const displayValue = currentPoint ? currentPoint.value : payload[0].value;

      // Unit label based on data type
      const unitLabel = dataType === 'sog' ? 'knots' : 'degrees';

      return (
        <div className="bg-card p-2 border border-border rounded shadow-md">
          <p className="font-semibold">{formatTooltipDate(displayLabel)}</p>
          <p>
            <span className="font-medium">
              {dataType === 'sog' ? 'Speed' : 'Course'}:
            </span>{' '}
            {displayValue} {unitLabel}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className={cn('w-full space-y-4 rounded-lg p-6', className)}>
      <ChartContainer
        config={{
          value: {
            label: dataLabel,
            color: 'hsl(var(--chart-1))',
          },
        }}
        className="h-[400px] w-full"
      >
        <LineChart
          data={chartData}
          margin={{ top: 10, right: 30, bottom: 60, left: 20 }}
          onMouseMove={handleChartMouseMove}
          onMouseLeave={handleChartMouseLeave}
          ref={chartRef}
        >
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey="timestamp"
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            tickFormatter={formatXAxis}
            angle={-45}
            textAnchor="end"
            height={60}
            label={{
              value: 'Date & Time',
              position: 'insideBottom',
              offset: -40,
            }}
          />
          <YAxis
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            domain={yAxisDomain}
            label={{
              value: dataLabel,
              angle: -90,
              position: 'insideLeft',
            }}
          />
          <Tooltip
            content={<CustomTooltipContent />}
            isAnimationActive={false}
          />
          <Line
            type="monotone"
            dataKey="value"
            name={dataType === 'sog' ? 'Speed' : 'Course'}
            stroke="var(--color-data, #00647a)"
            strokeWidth={3}
            dot={<CustomDot />}
            activeDot={{
              r: 8,
              fill: '#00A3C4',
              stroke: 'white',
              strokeWidth: 2,
            }}
            isAnimationActive={false}
          />
        </LineChart>
      </ChartContainer>
    </div>
  );
}
